/**
 * PersonHitCard — o card pessoa-cêntrica, compartilhado entre a busca da Inbox
 * e o painel direito do Localizador Universal (⌘K).
 *
 * Extraído de `InboxPersonSearchResults` na frente do ⌘K (D5 da spec
 * 2026-08-14): reuso de UM componente, não de UM lugar. Uma correção conserta
 * os dois consumidores, e o operador vê a mesma coisa nos dois.
 *
 * Origem do desenho (rodada 2 das Conversas Cliente-Cêntricas): o card NASCE
 * aberto — cabeçalho com identidade + «N conversas no total» REAL
 * (`meta.totalCount` do endpoint da ficha) + as 4 mais recentes inline + «ver as
 * N no histórico» + ações «Histórico completo» / «Abrir conversa ativa» (esta só
 * quando existe ativa — nunca oferecer botão que não leva a lugar).
 *
 * `collapsible`:
 *   - `true` (default) — comportamento da Inbox: dá ao operador o botão de
 *     recolher para caber mais pessoa na lista.
 *   - `false` — painel do Localizador: recolher não faz sentido, porque ali o
 *     card É o item em foco. Ele existe *porque* a linha está selecionada.
 *
 * Fidelidade DECLARADA vs mockup 01/2a: «X aguardando você» virou «X ativas»
 * (agregado por-assignee não existe barato — e ele lê a janela de 20 episódios,
 * não o banco, diferente do total, que é REAL); sem highlight do termo no nome;
 * sem «última há Y» (o endpoint de lista não traz).
 */
import { History } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UserAvatar } from '../../components/UserAvatar';

import {
  conversationOutcomeLabel,
  conversationStats,
  conversationStatusLabel,
  formatShortDate,
  TERMINAL_CONVERSATION_STATUSES,
} from './persons-adapter';
import { usePersonConversations } from './use-persons';

import type { PersonConversation, PersonListItem } from './types';

const RECENT_EPISODES = 4;

export function PersonHitCard({
  person,
  collapsible = true,
}: {
  person: PersonListItem;
  collapsible?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  // Decisão 1 da rodada 2 — o card NASCE aberto.
  const [openCard, setOpenCard] = useState(true);
  const isOpen = collapsible ? openCard : true;
  const { data } = usePersonConversations(person.id, true);
  const episodes = data?.data ?? [];
  const totalCount = data?.meta.totalCount;
  const stats = conversationStats(episodes);
  const active = episodes.find((e) => !TERMINAL_CONVERSATION_STATUSES.has(e.status));
  const recent = episodes.slice(0, RECENT_EPISODES);

  const goHistory = (): void => {
    navigate(`/pessoas/${person.id}#conversas`);
  };

  return (
    <div className="inbox-person-hit" data-testid={`person-hit-${person.id}`}>
      <div className="inbox-person-hit__head">
        {collapsible && (
          <button
            type="button"
            className="inbox-person-hit__collapse"
            onClick={() => setOpenCard((v) => !v)}
            aria-expanded={openCard}
            aria-label={openCard ? 'Recolher conversas da pessoa' : 'Expandir conversas da pessoa'}
          >
            {openCard ? '▾' : '▸'}
          </button>
        )}
        <UserAvatar name={person.displayName} colorKey={person.id} size="md" />
        <div className="inbox-person-hit__id">
          <span className="inbox-person-hit__name">{person.displayName}</span>
          <span className="inbox-person-hit__meta">
            {/* F.5 M2 — o identificador DESAMBIGUA homônimos: fica sempre,
                lado a lado com a contagem real. */}
            {person.primaryIdentifierValue !== null && (
              <span>{person.primaryIdentifierValue} · </span>
            )}
            {totalCount !== undefined && (
              <>
                <strong>{totalCount} conversas no total</strong>
                {stats.active > 0 && (
                  <span className="inbox-person-hit__active">
                    {' '}
                    · {stats.active} ativa{stats.active === 1 ? '' : 's'}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
        <span className="inbox-person-hit__spacer" />
        <button type="button" className="btn btn--secondary btn--sm" onClick={goHistory}>
          <History size={12} aria-hidden /> Histórico completo
        </button>
        {active !== undefined && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => navigate(`/atendimento/conversation/${active.id}`)}
          >
            Abrir conversa ativa
          </button>
        )}
      </div>
      {isOpen && recent.length > 0 && (
        <div className="inbox-person-hit__episodes">
          {recent.map((e) => (
            <EpisodeRow
              key={e.id}
              episode={e}
              onOpen={() => navigate(`/atendimento/conversation/${e.id}`)}
            />
          ))}
          {/* «Ver as N no histórico completo» é a MESMA rota do botão «Histórico
              completo» do cabeçalho — `goHistory` nos dois. Na Inbox a
              duplicação se justifica: com o card recolhido, o botão do cabeçalho
              é o único alcançável. No painel do Localizador o card está sempre
              aberto, então os dois aparecem a 30px um do outro fazendo a mesma
              coisa — redundância que o TL notou usando. Sai quando não há
              colapso. */}
          {collapsible && totalCount !== undefined && totalCount > recent.length && (
            <button type="button" className="inbox-person-hit__more" onClick={goHistory}>
              Ver as {totalCount} no histórico completo →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function EpisodeRow({
  episode,
  onOpen,
}: {
  episode: PersonConversation;
  onOpen: () => void;
}): JSX.Element {
  const isActive = !TERMINAL_CONVERSATION_STATUSES.has(episode.status);
  const label =
    episode.outcomeType !== undefined && episode.outcomeType !== null
      ? conversationOutcomeLabel(episode.outcomeType)
      : conversationStatusLabel(episode.status);
  return (
    <button
      type="button"
      className={`inbox-person-hit__episode${isActive ? ' inbox-person-hit__episode--active' : ''}`}
      onClick={onOpen}
    >
      <span
        className={`inbox-person-hit__chip${isActive ? ' inbox-person-hit__chip--active' : ''}`}
      >
        {isActive ? 'ativa' : label.toLowerCase()}
      </span>
      <span className="inbox-person-hit__excerpt">{episode.excerpt ?? label}</span>
      <span className="inbox-person-hit__date">{formatShortDate(episode.updatedAt)}</span>
    </button>
  );
}
